import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { readCart, writeCart } from "@/pages/Catalogue";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Trash2, Plus, ArrowLeft, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { SearchableSelect } from "@/components/SearchableSelect";

const emptyAddr = { label: "", line1: "", line2: "", city: "", state: "", pincode: "", gstin: "", kind: "both" };
const fmtAddr = (a) => `${a.label} · ${a.line1}, ${a.city}, ${a.state} - ${a.pincode}`;

export default function PoBuilder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState(readCart());
  const [products, setProducts] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [dealerId, setDealerId] = useState("");
  const [dealer, setDealer] = useState(null);
  const [billingId, setBillingId] = useState("");
  const [shippingId, setShippingId] = useState("");
  const [notes, setNotes] = useState("");
  const [newAddr, setNewAddr] = useState(emptyAddr);
  const [addAddrOpen, setAddAddrOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/dealers").then(({ data }) => {
    setDealers(data);
    if (user?.role === "dealer" && data.length) {
      setDealerId(data[0].dealer_id);
    }
  }); }, [user]);

  // Nothing is priced until dealer + billing + shipping are all locked in —
  // only then do we know which price list applies, so only then do we fetch
  // with dealer context and let totals compute.
  const pricingReady = Boolean(dealerId && billingId && shippingId);

  useEffect(() => {
    const ids = Object.keys(cart);
    if (ids.length === 0) { setProducts([]); return; }

    // Selecting a dealer flips `pricingReady` from false -> true across two
    // renders in a row (dealerId lands first, billingId/shippingId land a
    // moment later once /dealers/:id resolves). That means this effect can
    // fire twice back-to-back — once unpriced, once priced — and the two
    // requests race. Without this guard, if the *unpriced* request's
    // response happens to arrive after the priced one, its .then() stomps
    // setProducts() with dealer_price: null for everything, which is
    // exactly the "loads fine, then resets to price list not set" bug.
    // Standard fix: an `ignore` flag closed over by cleanup — the effect
    // that's no longer current marks its own in-flight response as stale.
    let ignore = false;
    const params = pricingReady ? { dealer_id: dealerId } : {};

    api.get("/products", { params }).then(({ data }) => {
      if (ignore) return; // a newer effect run already superseded this response
      const filtered = data.filter(p =>
        ids.includes(p.product_id) || ids.includes(p.sap_item_code) || ids.includes(p.sku)
      );
      setProducts(
        pricingReady
          ? filtered
          : filtered.map(p => ({ ...p, dealer_price: null, group_prices: undefined }))
      );
    });

    return () => { ignore = true; };
  }, [cart, dealerId, pricingReady]);

  useEffect(() => {
    if (!dealerId) { setDealer(null); return; }
    api.get(`/dealers/${dealerId}`).then(({ data }) => {
      setDealer(data);
    });
  }, [dealerId]);

  // Sales reps are assigned specific SHIPPING locations, not whole dealers —
  // billing addresses are deliberately never assignable (they're shared/
  // finance info, not a sales officer's territory), so billing must stay
  // unrestricted or a sales rep would have zero valid billing options and
  // could never submit a PO at all.
  const availableBilling = useMemo(() => {
    return (dealer?.addresses || []).filter(a => a.kind !== "shipping");
  }, [dealer]);

  const availableShipping = useMemo(() => {
    const shipping = (dealer?.addresses || []).filter(a => a.kind !== "billing");
    if (user?.role !== "sales") return shipping;
    const allowed = new Set(user.assigned_address_ids || []);
    return shipping.filter(a => allowed.has(a.id));
  }, [dealer, user]);

  useEffect(() => {
    if (!dealer) return;
    setBillingId(availableBilling[0]?.id || "");
    setShippingId(availableShipping[0]?.id || "");
  }, [dealer, availableBilling, availableShipping]);

  const setQty = (pid, qty) => {
    const next = { ...cart };
    if (qty <= 0) delete next[pid]; else next[pid] = qty;
    setCart(next); writeCart(next);
  };

  const totals = useMemo(() => {
    if (!pricingReady) return { subtotal: null, gst: null, total: null };
    // Always the resolved price-list price — never a fallback to MRP.
    const subtotal = products.reduce((s, p) => s + (cart[p.product_id] || 0) * (p.dealer_price ?? 0), 0);
    const gst = subtotal * 0.18;
    return { subtotal, gst, total: subtotal + gst };
  }, [cart, products, pricingReady]);

  const addAddress = async () => {
    if (!newAddr.label || !newAddr.line1 || !newAddr.city) { toast.error("Fill label, line 1 and city"); return; }
    try {
      const { data } = await api.post(`/dealers/${dealerId}/addresses`, newAddr);
      setDealer(d => ({ ...d, addresses: [...d.addresses, data] }));
      setNewAddr(emptyAddr);
      setAddAddrOpen(false);
      toast.success("Address added");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submit = async (asDraft = false) => {
    if (!dealerId) return toast.error("Select a dealer");
    if (!billingId || !shippingId) return toast.error("Select addresses");
    const lines = products.map(p => ({ product_id: p.product_id, quantity: cart[p.product_id] || 0 })).filter(l => l.quantity > 0);
    if (lines.length === 0) return toast.error("Cart is empty");

    if (!asDraft) {
      const overStock = products.find(p => {
        const q = cart[p.product_id] || 0;
        const avail = p.available_stock !== undefined ? p.available_stock : (p.stock || 0);
        return q > avail;
      });
      if (overStock) {
        const avail = overStock.available_stock !== undefined ? overStock.available_stock : (overStock.stock || 0);
        return toast.error(`Cannot submit: Requested qty for ${overStock.name} exceeds available stock (${avail}).`);
      }

      // Strict Credit Limit Validation
      const creditLimit = parseFloat(dealer?.credit_limit || 0);
      const currentOutstanding = parseFloat(dealer?.current_outstanding || 0);
      const availableCredit = Math.max(0, creditLimit - currentOutstanding);

      if (creditLimit > 0 && totals.total > availableCredit) {
        return toast.error(
          `Order Blocked: Cart total (${fmtINR(totals.total)}) exceeds your available credit (${fmtINR(availableCredit)}). Please clear outstanding balance with Accounts.`
        );
      }
    }

    setSubmitting(true);
    try {
      const { data } = await api.post("/po", {
        dealer_id: dealerId, billing_address_id: billingId, shipping_address_id: shippingId,
        lines, notes, submit: !asDraft,
      });
      writeCart({}); setCart({});
      toast.success(`PO ${data.po_number} ${asDraft ? "saved as draft" : "submitted"}`);
      navigate(`/po/${data.po_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  const cartCount = Object.values(cart).reduce((a,b) => a+b, 0);

  return (
    <div className="space-y-6" data-testid="po-builder-page">
      <button onClick={() => navigate("/catalogue")} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-to-cat">
        <ArrowLeft size={14} /> Back to catalogue
      </button>
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">New Purchase Order</div>
        <h1 className="mt-1 text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>Review & submit</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-4">
          <Card className="p-6">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-4">Line items ({cartCount})</div>
            {products.length === 0 && (
              <div className="py-10 text-center text-sm text-slate-500">
                Cart is empty. <button className="text-blue-600" onClick={() => navigate("/catalogue")}>Add products</button>.
              </div>
            )}
            <div className="divide-y divide-slate-100">
              {products.map(p => {
                const qty = cart[p.product_id] || 0;
                const avail = p.available_stock !== undefined ? p.available_stock : (p.stock || 0);
                const isOver = qty > avail;
                return (
                  <div key={p.product_id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border-b border-slate-50 last:border-0" data-testid={`line-${p.sku}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-xs sm:text-sm line-clamp-2">{p.name}</div>
                      <div className="text-[10px] sm:text-xs text-slate-500 font-mono mt-1 flex flex-wrap items-center gap-1.5 sm:gap-2">
                        <span>{p.sku}</span>
                        {user?.role === "dealer" ? (
                          <span className={`px-1.5 py-0.5 rounded ${isOver || avail <= 0 ? "bg-rose-100 text-rose-700 font-semibold" : "bg-emerald-50 text-emerald-700 font-medium"}`}>
                            {avail > 0 ? "In Stock" : "Out of Stock"}
                          </span>
                        ) : (
                          <span className={`px-1.5 py-0.5 rounded ${isOver ? "bg-rose-100 text-rose-700 font-semibold" : "bg-slate-100 text-slate-600"}`}>
                            Avail: {avail}
                          </span>
                        )}
                      </div>
                      {isOver && (
                        <div className="text-[10px] sm:text-[11px] text-rose-600 font-medium mt-0.5">
                          {user?.role === "dealer" ? "Requested quantity exceeds available stock" : `Exceeds available stock (${avail})`}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 pl-14 sm:pl-0 w-full sm:w-auto">
                      <div className="flex items-center border border-slate-200 rounded-md bg-white">
                        <button onClick={() => setQty(p.product_id, qty - 1)} className="px-2.5 py-1 sm:px-3 sm:py-1.5 hover:bg-slate-50">−</button>
                        <input
                          type="number"
                          min="0"
                          value={qty}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            setQty(p.product_id, isNaN(val) ? 0 : val);
                          }}
                          className="w-14 text-center text-xs sm:text-sm font-medium border-0 focus:ring-0 focus:outline-none p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          data-testid={`builder-qty-input-${p.sku}`}
                        />
                        <button onClick={() => setQty(p.product_id, qty + 1)} className="px-2.5 py-1 sm:px-3 sm:py-1.5 hover:bg-slate-50">+</button>
                      </div>
                      <div className="text-right flex-1 sm:w-28 flex flex-row sm:flex-col justify-end sm:justify-center items-center sm:items-end gap-2 sm:gap-0">
                      {!pricingReady ? (
                        <div className="text-xs text-slate-400">Select dealer &amp; addresses</div>
                      ) : p.dealer_price === null || p.dealer_price === undefined ? (
                        <div className="text-xs text-rose-600">No price list price</div>
                      ) : (
                        <>
                          <div className="text-sm font-medium">{fmtINR(qty * p.dealer_price)}</div>
                          <div className="text-xs text-slate-500">@ {fmtINR(p.dealer_price)}</div>
                        </>
                      )}
                      </div>
                      <button onClick={() => setQty(p.product_id, 0)} className="p-1.5 sm:p-2 hover:bg-rose-50 text-rose-600 rounded-md shrink-0" data-testid={`remove-${p.sku}`}><Trash2 size={14} className="sm:w-4 sm:h-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-6">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-4">Order notes</div>
            <Textarea value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder="Delivery preferences, PO reference, etc." rows={3} data-testid="po-notes" />
          </Card>
        </div>

        <div className="lg:col-span-4 space-y-4">
          <Card className="p-6 sticky top-20">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-4">Dealer & shipping</div>

            <label className="text-xs text-slate-600">Dealer</label>
            {user?.role === "dealer" ? (
              <div className="mt-1 h-10 border border-slate-200 rounded-md px-3 flex items-center bg-slate-50 text-sm" data-testid="dealer-locked">
                {dealer?.name || "…"} <span className="ml-auto text-xs text-slate-500">{dealer?.code}</span>
              </div>
            ) : (
              <SearchableSelect 
                value={dealerId} 
                onChange={setDealerId} 
                options={dealers.map(d => ({ value: d.dealer_id, label: `${d.name} · ${d.area}` }))} 
                placeholder="Select dealer..." 
                className="mt-1" 
              />
            )}

            {dealer && (
              <>
                <div className="mt-4 flex items-center justify-between">
                  <label className="text-xs text-slate-600">Billing address</label>
                  <Dialog open={addAddrOpen} onOpenChange={setAddAddrOpen}>
                    <DialogTrigger asChild>
                      <button className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1" data-testid="add-addr-btn"><Plus size={12} /> Add new</button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>New address for {dealer.name}</DialogTitle></DialogHeader>
                      <div className="grid grid-cols-2 gap-3">
                        <Input placeholder="Label (e.g. Warehouse A)" value={newAddr.label} onChange={e=>setNewAddr({...newAddr,label:e.target.value})} data-testid="addr-label" />
                        <Select value={newAddr.kind} onValueChange={v=>setNewAddr({...newAddr,kind:v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="both">Billing + Shipping</SelectItem>
                            <SelectItem value="billing">Billing only</SelectItem>
                            <SelectItem value="shipping">Shipping only</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input placeholder="Line 1" className="col-span-2" value={newAddr.line1} onChange={e=>setNewAddr({...newAddr,line1:e.target.value})} data-testid="addr-line1" />
                        <Input placeholder="Line 2" className="col-span-2" value={newAddr.line2} onChange={e=>setNewAddr({...newAddr,line2:e.target.value})} />
                        <Input placeholder="City" value={newAddr.city} onChange={e=>setNewAddr({...newAddr,city:e.target.value})} data-testid="addr-city" />
                        <Input placeholder="State" value={newAddr.state} onChange={e=>setNewAddr({...newAddr,state:e.target.value})} />
                        <Input placeholder="Pincode" value={newAddr.pincode} onChange={e=>setNewAddr({...newAddr,pincode:e.target.value})} />
                        <Input placeholder="GSTIN" value={newAddr.gstin} onChange={e=>setNewAddr({...newAddr,gstin:e.target.value})} />
                      </div>
                      <DialogFooter><Button onClick={addAddress} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="save-addr-btn"><PackagePlus size={14} className="mr-2" />Save address</Button></DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
                <SearchableSelect 
                  value={billingId} 
                  onChange={setBillingId} 
                  options={availableBilling.map(a => ({ value: a.id, label: fmtAddr(a) }))} 
                  placeholder="Select..." 
                  className="mt-1" 
                />

                <label className="text-xs text-slate-600 mt-3 block">Shipping address</label>
                {user?.role === "sales" && availableShipping.length === 1 ? (
                  <div className="mt-1 border border-slate-200 rounded-md px-3 py-2 flex items-center bg-slate-50 text-sm line-clamp-2" data-testid="shipping-locked">
                    {fmtAddr(availableShipping[0])}
                  </div>
                ) : (
                  <SearchableSelect 
                    value={shippingId} 
                    onChange={setShippingId} 
                    options={availableShipping.map(a => ({ value: a.id, label: fmtAddr(a) }))} 
                    placeholder="Select..." 
                    className="mt-1" 
                  />
                )}
                {user?.role === "sales" && availableShipping.length === 0 && (
                  <p className="text-xs text-red-600 mt-2">You have no assigned shipping locations for this dealer — ask an admin to assign one before you can order here.</p>
                )}
              </>
            )}

            {dealer && (
              <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-md space-y-1.5 text-xs">
                <div className="flex justify-between text-slate-600">
                  <span>Credit Limit:</span>
                  <span className="font-semibold text-slate-800">
                    {parseFloat(dealer.credit_limit || 0) > 0 ? fmtINR(dealer.credit_limit) : "Unrestricted"}
                  </span>
                </div>
                {parseFloat(dealer.credit_limit || 0) > 0 && (
                  <>
                    <div className="flex justify-between text-slate-600">
                      <span>Current Outstanding:</span>
                      <span className="font-medium text-amber-700">{fmtINR(dealer.current_outstanding || 0)}</span>
                    </div>
                    <div className="flex justify-between pt-1 border-t border-slate-200 font-semibold">
                      <span>Available Credit:</span>
                      <span className={Math.max(0, parseFloat(dealer.credit_limit || 0) - parseFloat(dealer.current_outstanding || 0)) < (totals.total || 0) ? "text-rose-600" : "text-emerald-700"}>
                        {fmtINR(Math.max(0, parseFloat(dealer.credit_limit || 0) - parseFloat(dealer.current_outstanding || 0)))}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-slate-200 text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{pricingReady ? fmtINR(totals.subtotal) : "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">GST 18%</span><span>{pricingReady ? fmtINR(totals.gst) : "—"}</span></div>
              <div className="flex justify-between text-lg font-medium pt-2"><span>Total</span><span data-testid="po-total">{pricingReady ? fmtINR(totals.total) : "—"}</span></div>
              {!pricingReady && (
                <p className="text-xs text-slate-500 pt-1">Select a dealer, billing address, and shipping address to see pricing.</p>
              )}
            </div>

            {/* Credit Limit Breached Warning Banner */}
            {pricingReady && parseFloat(dealer?.credit_limit || 0) > 0 && (totals.total || 0) > Math.max(0, parseFloat(dealer?.credit_limit || 0) - parseFloat(dealer?.current_outstanding || 0)) && (
              <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-md text-xs text-rose-800 space-y-1">
                <div className="font-semibold flex items-center gap-1.5 text-rose-900">
                  ⛔ Order Blocked: Credit Limit Exceeded
                </div>
                <div>
                  Cart total ({fmtINR(totals.total)}) exceeds available credit ({fmtINR(Math.max(0, parseFloat(dealer.credit_limit) - parseFloat(dealer.current_outstanding || 0)))}).
                </div>
                <div className="text-[11px] text-rose-700">
                  Please clear outstanding balance with Accounts to release credit.
                </div>
              </div>
            )}

            <div className="mt-5 space-y-2">
              <Button 
                onClick={() => submit(false)} 
                disabled={
                  submitting || 
                  cartCount === 0 || 
                  !pricingReady || 
                  (parseFloat(dealer?.credit_limit || 0) > 0 && (totals.total || 0) > Math.max(0, parseFloat(dealer?.credit_limit || 0) - parseFloat(dealer?.current_outstanding || 0)))
                } 
                className="w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50 disabled:cursor-not-allowed" 
                data-testid="submit-po-btn"
              >
                {submitting ? "Submitting..." : "Submit Purchase Order"}
              </Button>
              <Button onClick={() => submit(true)} disabled={submitting || cartCount===0 || !pricingReady} variant="outline" className="w-full h-10" data-testid="save-draft-btn">
                Save as draft
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}